import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Link, Navigate, useOutletContext, useParams } from "react-router-dom";
import { realCaseStudies } from "@/components/equinox/data";

export default function CaseStudyDetailPage() {
  const { slug } = useParams();
  const { onPrimaryAction } = useOutletContext();
  const study = realCaseStudies.find((item) => item.slug === slug);

  if (!study) {
    return <Navigate to="/case-studies" replace />;
  }

  return (
    <section className="px-6 pb-16 pt-14 sm:px-8 lg:px-12">
      <div className="mx-auto max-w-6xl">
        <Link to="/case-studies" className="inline-flex items-center gap-2 text-sm text-white/[0.62] transition-colors hover:text-white" data-testid="case-study-back-link">
          <ArrowLeft className="h-4 w-4" />
          Back to Case Studies
        </Link>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-8 grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="text-xs uppercase tracking-[0.26em] text-white/[0.42]">{study.type} • {study.client}</p>
            <h1 className="mt-4 font-display text-4xl text-white sm:text-5xl">{study.headline}</h1>
            <p className="mt-5 text-base leading-8 text-white/[0.66]">{study.summary}</p>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <div className="rounded-[1.5rem] border border-white/[0.08] bg-black/20 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-white/[0.4]">Challenge</p>
                <p className="mt-4 text-sm leading-7 text-white/[0.72]">{study.problem}</p>
              </div>
              <div className="rounded-[1.5rem] border border-white/[0.08] bg-black/20 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-white/[0.4]">What Equinox Did</p>
                <p className="mt-4 text-sm leading-7 text-white/[0.72]">{study.solution}</p>
              </div>
            </div>

            <div className="mt-8 space-y-3">
              {study.outcomes.map((outcome) => (
                <div key={outcome} className="glass-panel cinematic-card rounded-[1.4rem] px-5 py-4 text-sm text-white/[0.74]">
                  {outcome}
                </div>
              ))}
            </div>
          </div>

          <div className="glitter-card glass-panel rounded-[2rem] p-6 lg:p-7">
            <p className="text-xs uppercase tracking-[0.26em] text-white/[0.42]">Results</p>
            <div className="mt-5 grid gap-4">
              {study.numbers.map((number) => (
                <div key={number} className="rounded-[1.35rem] border border-white/[0.08] bg-black/25 p-5">
                  <p className="font-display text-3xl text-white">{number}</p>
                </div>
              ))}
            </div>
            <div className="mt-4 rounded-[1.35rem] border border-white/[0.08] bg-white/[0.04] p-5">
              <p className="text-xs uppercase tracking-[0.2em] text-white/[0.42]">Verified public proof</p>
              <div className="mt-4 space-y-3 text-sm text-white/[0.72]">
                {study.results.map((result) => (
                  <div key={result}>{result}</div>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={onPrimaryAction}
              className="glass-button glass-panel mt-6 rounded-full px-5 py-4 text-sm font-medium text-white transition-all duration-300 hover:scale-[1.03]"
              data-testid="case-study-detail-ai-plan-button"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                Book a Growth Call
                <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}